import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Users, MapPin, Clock, Calendar, UserPlus } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useAssignKidToBatch } from "@/hooks/useAssignKidToBatch";
import { asArray } from "@/lib/utils";

interface Batch {
  id: number;
  name: string;
  ageGroup: string;
  ageRange: string;
  skillLevel: string | null;
  ballType: string | null;
  days: string[] | null;
  timeSlot: string | null;
  sessionsPerWeek: number | null;
  schedule: string | null;
  venue: string | null;
  maxCapacity: number | null;
  currentEnrollment: number;
  isActive: boolean;
}

interface BatchKid {
  id: string;
  fullName: string;
  age: number;
  parentName: string;
}

interface EligiblePlayer {
  id: number;
  firstName: string;
  lastName: string;
  batchId: number | null;
  pendingCoachReview: boolean;
}

export default function BatchesPage() {
  const navigate = useNavigate();
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [addPlayerOpen, setAddPlayerOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState<{ player: EligiblePlayer; fromBatchName: string } | null>(null);

  const assignKid = useAssignKidToBatch();

  // staleTime: 0 overrides the app-wide default (queryClient.ts sets
  // staleTime: Infinity globally) — this page must always refetch on
  // mount/revisit. With the global default, once this exact query key was
  // fetched successfully once in a browser tab, React Query treated it as
  // permanently fresh and never updated the render again, no matter what
  // changed server-side, until a full page reload. Real incident: a tab
  // that loaded this page during a since-fixed empty/wrong-data window on
  // the batches table stayed frozen on that stale result indefinitely.
  // Batches are actively mutated elsewhere (archive/restore, enroll,
  // create) by other users/tabs, so this page can't trust an
  // indefinitely-cached result the way genuinely static data could.
  const { data: batchesData, isLoading, error } = useQuery({
    queryKey: ["/api/batches"],
    queryFn: async () => apiRequest("GET", "/api/batches"),
    staleTime: 0,
  });
  const batches: Batch[] = asArray<Batch>(batchesData?.data);

  const selectedBatch = batches.find((b) => b.id === selectedBatchId) ?? null;

  // "View details" was wired correctly (setSelectedBatchId) but the panel
  // it reveals renders below the full "All Squads" grid — with up to 16
  // cards above it, clicking the button on an early card produced no
  // visible change without the user manually scrolling, which reads as a
  // dead button. Scroll the panel into view whenever a batch is selected.
  const detailsPanelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selectedBatchId !== null) {
      detailsPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedBatchId]);

  const { data: kidsData, isLoading: kidsLoading } = useQuery({
    queryKey: ["/api/batches", selectedBatchId, "kids"],
    queryFn: async () => apiRequest("GET", `/api/batches/${selectedBatchId}/kids`),
    enabled: selectedBatchId !== null,
  });
  const batchKids: BatchKid[] = asArray<BatchKid>(kidsData?.data);

  // Lazy — only fetched once the Add Player dialog is actually opened.
  const { data: playersData } = useQuery({
    queryKey: ["/api/players"],
    queryFn: async () => apiRequest("GET", "/api/players"),
    enabled: addPlayerOpen,
  });
  const eligiblePlayers: EligiblePlayer[] = asArray<EligiblePlayer>(playersData?.data).filter(
    (p: EligiblePlayer) => !p.pendingCoachReview && p.batchId !== selectedBatchId,
  );

  function handleSelectPlayer(player: EligiblePlayer) {
    if (!selectedBatch) return;
    setAddPlayerOpen(false);

    if (player.batchId) {
      const fromBatch = batches.find((b) => b.id === player.batchId);
      setPendingMove({ player, fromBatchName: fromBatch?.name ?? "another batch" });
      return;
    }

    assignKid.mutate({ batchId: selectedBatch.id, kidId: player.id });
  }

  function confirmMove() {
    if (!selectedBatch || !pendingMove) return;
    assignKid.mutate({ batchId: selectedBatch.id, kidId: pendingMove.player.id });
    setPendingMove(null);
  }

  const atCapacity = !!selectedBatch
    && selectedBatch.maxCapacity !== null
    && selectedBatch.currentEnrollment >= selectedBatch.maxCapacity;

  return (
    <div
      data-testid="coach-batches-page"
      className="px-4 py-6 md:px-6 md:py-8 max-w-5xl mx-auto"
    >
      {/* Header */}
      <div data-testid="coach-batches-header" className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          My Squads
        </h1>
        <p className="text-sm md:text-base text-gray-600">
          Browse your squads, see who's in each, and assign players.
        </p>
      </div>

      {isLoading && (
        <div className="text-center py-12 text-gray-500" data-testid="batches-loading">
          Loading squads…
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          Failed to load squads. Please refresh the page.
        </div>
      )}

      {/* Batches List */}
      {!isLoading && !error && (
        <div className="mb-6 md:mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            All Squads
          </h2>
          <div
            data-testid="coach-batches-list"
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {batches.map((batch) => (
              <div
                key={batch.id}
                data-testid="coach-batch-card"
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-5 hover:shadow-md transition-shadow"
              >
                <div className="space-y-3">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {batch.name}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {batch.ageGroup}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {batch.skillLevel ?? "—"}
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>{batch.schedule || "—"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{batch.venue || "—"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Users className="h-4 w-4" />
                      <span className="font-semibold text-gray-900">
                        {batch.currentEnrollment}/{batch.maxCapacity ?? "—"}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => setSelectedBatchId(batch.id)}
                    >
                      View details
                    </Button>
                    <Link
                      to={`/coach/batches/${batch.id}/analytics`}
                      className="flex-1"
                      data-testid={`btn-batch-analytics-${batch.id}`}
                    >
                      <Button variant="secondary" size="sm" className="w-full">
                        Analytics →
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Batch Details Panel */}
      <div className="mt-6 md:mt-8" ref={detailsPanelRef}>
        {selectedBatch ? (
          <div
            data-testid="coach-batch-details"
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6"
          >
            <div className="space-y-4">
              <div className="border-b border-gray-200 pb-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                  {selectedBatch.name}
                </h2>
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge variant="secondary">{selectedBatch.ageGroup}</Badge>
                  <Badge variant="outline">{selectedBatch.skillLevel ?? "—"}</Badge>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>{selectedBatch.schedule || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <span>{selectedBatch.venue || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span>
                      {selectedBatch.currentEnrollment}/{selectedBatch.maxCapacity ?? "—"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Kids in Batch */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Kids in this batch ({batchKids.length})
                  </h3>
                  <Button
                    size="sm"
                    onClick={() => setAddPlayerOpen(true)}
                    disabled={atCapacity}
                    data-testid="btn-add-player"
                    title={atCapacity ? "This batch is at capacity" : undefined}
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    Add Player
                  </Button>
                </div>
                {atCapacity && (
                  <p className="text-sm text-amber-600 mb-3">
                    This batch is at capacity — remove a player or choose a different batch.
                  </p>
                )}
                {kidsLoading ? (
                  <div className="text-sm text-gray-500 py-4">Loading kids…</div>
                ) : batchKids.length === 0 ? (
                  <div className="text-sm text-gray-500 py-4">No kids assigned yet.</div>
                ) : (
                  <div className="space-y-2">
                    {batchKids.map((kid) => (
                      <div
                        key={kid.id}
                        data-testid="coach-batch-kid-item"
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors"
                        onClick={() => navigate(`/coach/players/${kid.id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {kid.fullName}
                            </p>
                            <p className="text-sm text-gray-600">Age {kid.age}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Availability tracking lives on the real Schedule page (reads
                  actual session_availability rows via GET /api/coach/sessions),
                  not here — this panel used to render entirely mock data
                  (@/mock/availability), so it could never reflect a real
                  parent RSVP. Removed rather than wired up: a batch doesn't
                  map 1:1 to "sessions" the way this panel assumed, and doing
                  that properly overlaps with the still-open Teams/Squads
                  redesign. */}
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm text-gray-600">
                  Looking for RSVPs and availability?{" "}
                  <Link to="/coach/schedule" className="text-blue-600 hover:underline font-medium">
                    View the Schedule page
                  </Link>{" "}
                  for real-time attendance and availability.
                </p>
              </div>

              <Button
                variant="outline"
                className="w-full md:w-auto"
                onClick={() => setSelectedBatchId(null)}
              >
                Close details
              </Button>
            </div>
          </div>
        ) : (
          !isLoading && !error && (
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Select a batch to view details.</p>
            </div>
          )
        )}
      </div>

      {/* Add Player dialog */}
      <Dialog open={addPlayerOpen} onOpenChange={setAddPlayerOpen}>
        <DialogContent className="p-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle>Add Player to {selectedBatch?.name}</DialogTitle>
          </DialogHeader>
          <Command>
            <CommandInput placeholder="Search players by name…" />
            <CommandList>
              <CommandEmpty>No eligible players found.</CommandEmpty>
              <CommandGroup>
                {eligiblePlayers.map((player) => (
                  <CommandItem
                    key={player.id}
                    value={`${player.firstName} ${player.lastName}`}
                    onSelect={() => handleSelectPlayer(player)}
                    data-testid={`add-player-option-${player.id}`}
                  >
                    <span>{player.firstName} {player.lastName}</span>
                    {player.batchId && (
                      <span className="ml-auto text-xs text-gray-400">
                        currently assigned
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>

      {/* Confirm-on-add dialog — enrollment is additive now
          (POST /api/batches/:id/kids/:kidId writes to the
          batch_enrollments join table instead of overwriting the kid's
          single players.batch_id), so a second batch is a genuine
          addition, not a move. This confirm step still exists because
          it's a real action worth a pause, not because anything is being
          vacated. */}
      <AlertDialog open={!!pendingMove} onOpenChange={(open) => !open && setPendingMove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add player to another team?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingMove?.player.firstName} is already in {pendingMove?.fromBatchName}. Add them to {selectedBatch?.name} too — this won't remove them from {pendingMove?.fromBatchName}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingMove(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMove}>Add</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
